export const HITLAB_PROFILE_READY_SOURCE = 'hitlab-profile-ready';
export const HITLAB_RECOVERY_EMAIL_SOURCE = 'hitlab-recovery-email';
export const HITLAB_HOME_RESUME_SOURCE = 'hitlab-home-resume';
export const HITLAB_PROFILE_READY_MARKER_VERSION = 1;

export const HITLAB_PROFILE_READY_HOME_PATH = `/home?source=${HITLAB_PROFILE_READY_SOURCE}`;
export const HITLAB_RECOVERY_EMAIL_HOME_PATH = `/home?source=${HITLAB_RECOVERY_EMAIL_SOURCE}`;

export const HITLAB_PROFILE_READY_PAYMENT_PATH =
  `/hitlab/intake?step=payment&source=${HITLAB_PROFILE_READY_SOURCE}`;
export const HITLAB_RECOVERY_EMAIL_PAYMENT_PATH =
  `/hitlab/intake?step=payment&source=${HITLAB_RECOVERY_EMAIL_SOURCE}`;
export const HITLAB_HOME_RESUME_PAYMENT_PATH =
  `/hitlab/intake?step=payment&source=${HITLAB_HOME_RESUME_SOURCE}`;

export const HITLAB_ALLOWED_HOME_AUTH_RETURNS = new Set([
  HITLAB_PROFILE_READY_HOME_PATH,
  HITLAB_RECOVERY_EMAIL_HOME_PATH,
]);

const HITLAB_PROFILE_READY_MARKER_PREFIX = 'hitlabProfileReady:v1:';

const TERMINAL_PROFILE_READY_STATUSES = new Set([
  'paid',
  'paid_pending_upload',
  'submitted',
  'uploaded_pending_rights_screen',
  'ready_for_curator_listen',
  'hitlab_candidate',
  'street_drop_candidate',
  'hold',
  'rejected',
  'archived',
  'cancelled',
  'canceled',
]);

const safeMarkerSegment = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);

export const getHitLabPlayerId = (player) =>
  String(
    player?.data?.player?._id ||
      player?.data?.player?.id ||
      player?.data?.player?.playerId ||
      player?._id ||
      player?.id ||
      player?.playerId ||
      '',
  ).trim();

export const getHitLabProfileReadyMarkerKey = (playerId) => {
  const safePlayerId = safeMarkerSegment(playerId);
  return safePlayerId ? `${HITLAB_PROFILE_READY_MARKER_PREFIX}${safePlayerId}` : '';
};

export const parseHitLabProfileReadyMarker = (value) => {
  try {
    const parsed = JSON.parse(value || '');
    if (
      parsed?.version !== HITLAB_PROFILE_READY_MARKER_VERSION ||
      parsed?.active !== true ||
      !Number.isFinite(Number(parsed?.timestamp))
    ) {
      return null;
    }
    return {
      version: HITLAB_PROFILE_READY_MARKER_VERSION,
      active: true,
      timestamp: Number(parsed.timestamp),
    };
  } catch (err) {
    return null;
  }
};

export const writeHitLabProfileReadyMarker = (
  playerId,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
) => {
  const key = getHitLabProfileReadyMarkerKey(playerId);
  if (!key || !storage) return false;
  try {
    storage.setItem(
      key,
      JSON.stringify({
        version: HITLAB_PROFILE_READY_MARKER_VERSION,
        active: true,
        timestamp: Date.now(),
      }),
    );
    return true;
  } catch (err) {
    return false;
  }
};

export const clearHitLabProfileReadyMarker = (
  playerId,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
) => {
  const key = getHitLabProfileReadyMarkerKey(playerId);
  if (!key || !storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch (err) {
    return false;
  }
};

export const hasHitLabProfileReadyMarker = (
  playerId,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
) => {
  const key = getHitLabProfileReadyMarkerKey(playerId);
  if (!key || !storage) return false;
  try {
    return !!parseHitLabProfileReadyMarker(storage.getItem(key));
  } catch (err) {
    return false;
  }
};

export const isHitLabHomeResumeActivationSource = (source) =>
  source === HITLAB_RECOVERY_EMAIL_SOURCE ||
  source === HITLAB_PROFILE_READY_SOURCE;

export const isHitLabHomeResumeActivationSearch = (search = '') => {
  const params = new URLSearchParams(search || '');
  const entries = Array.from(params.entries());
  if (entries.length !== 1) return false;
  const [[key, value]] = entries;
  return key === 'source' && isHitLabHomeResumeActivationSource(value);
};

export const normalizeHitLabIntakeStatus = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

export const isHitLabProfileCompleteForResume = (intake = {}) =>
  !!String(intake?.artistName || '').trim();

export const isHitLabProfileReadyResumeIntake = (intake = {}) => {
  if (!isHitLabProfileCompleteForResume(intake)) return false;
  const status = normalizeHitLabIntakeStatus(intake?.status);
  const paymentStatus = normalizeHitLabIntakeStatus(intake?.paymentStatus);
  if (TERMINAL_PROFILE_READY_STATUSES.has(status)) return false;
  if (paymentStatus === 'paid' || paymentStatus === 'succeeded') return false;
  return status === 'pending_payment' && (!paymentStatus || paymentStatus === 'pending');
};

export const shouldClearHitLabProfileReadyMarkerForIntake = (intake = {}) =>
  !isHitLabProfileReadyResumeIntake(intake);
